import React from 'react'
import {clone} from 'lodash'
import {
  Card,
  CardActions,
  CardText,
  CardTitle
} from 'material-ui-legacy/Card'
import Row from 'muicss/lib/react/row'
import Col from 'muicss/lib/react/col'

import RelationalTextInput from '../UI/RelationalTextInput'
import ValidatedTextInput from '../UI/ValidatedTextInput'
import SaveButton from '../UI/SaveButton'
import CancelButton from '../UI/CancelButton'

import NewAgency from '../Agencies/NewAgency'

import Resource from '../../stores/resource'
const AgencyStore = Resource('agencies')

/*
Props:
- agencyProducer: a complete agencyProducer object
  or
- AgencyProducer fields: will be inserted in the form when present
- submitCallback: callback function called on form submission. This is *only*
  called when the form is valid and the contents have changed. Receives agencyProducer
  attributes.
- cancelCallback: callback function for the cancel button.
- changedCallback: callback function called when the form contents have changed.
  Receives true if the contents have changed.
- forceChanged: force the form status to "changed"
*/
class AgencyProducerForm extends React.PureComponent {
  constructor(props) {
    super(props)
    // We have to build the state on a regular variable so
    // React doesn't complain about direct state mutation.
    let initialState = {
      formValid: false,
      formChanged: (this.props.forceChanged || false),
      formSubmitted: false
    }

    // We can either receive a single data prop or each field separately
    // To make our lives easier, we unpack included data here.
    if (this.props.agencyProducer) {
      this.initialData = this.props.agencyProducer.attributes

      // Related fields
      this.initialData.agencyName =
        (this.initialData.agencyId) ?
        this.props.agencyProducer.included('agency').attributes.name : ''
    }
    else {
      this.initialData = clone(this.props)
    }

    const phones = this.initialData.phones
    this.initialData.mobilePhone = (phones ? phones.mobile : '')
    this.initialData.officePhone = (phones ? phones.office : '')

    this.fields = [
      'firstName',
      'lastName',
      'title',
      'email',
      'address',
      'city',
      'province',
      'postalCode',
      'country',
      'mobilePhone',
      'officePhone',
      'agencyName',
      'agencyId'
    ]
    for (let field of this.fields) {
      initialState[field] = {
        value: (this.initialData[field] || ''),
        valid: true
      }
    }

    // Phones need tweaking
    if (this.initialData.phones) {
      initialState.mobilePhone = {
        value: this.initialData.phones.mobile,
        valid: true
      }
      initialState.officePhone = {
        value: this.initialData.phones.office,
        valid: true
      }
    }

    // Et voilà!
    this.state = initialState

    // Events to close relational forms
    this.closeAddAgencyEvent = new Event('closeAddAgencyEvent', {bubbles: true})
  }

  componentDidMount() {
    if (this.props.forceChanged) this._validateForm()
  }

  _submit = () => {
    // Don't do anything if we have invalid fields or no changes.
    if (!this.state.formValid || !this.state.formChanged) return

    let attributes = {}
    for (let field of this.fields) {
      attributes[field] = this.state[field].value
    }
    this.props.submitCallback(attributes)
    this.setState({formChanged: false, formSubmitted: true})
  }

  _change = (value, valid, attribute) => {
    let updates = {}
    updates[attribute] = {value: value, valid: valid}
    this.setState(
      updates,
      () => {
        this._checkForChanges()
        this._validateForm()
      }
    )
  }

  _setRelationalName = (textField, idField, value) => {
    let change = {}
    // Relations aren't required in this form, so we need to handle that
    if (value.trim() === '') {
      change[textField] = {value: '', valid: true}
      change[idField] = {value: '', valid: true}
    }
    else {
      change[textField] = {value: value, valid: false}
      change[idField] = {value: '', valid: false}
    }
    this.setState(
      change,
      () => {
        this._checkForChanges()
        this._validateForm()
      }
    )
  }

  _checkForChanges = () => {
    let changed = (this.props.forceChanged || false)
    for (let field of this.fields) {
      let current = this.state[field].value
      let original = this.initialData[field] || ''
      if (current !== original) {
        changed = true
        break
      }
    }
    this.setState(
      {formChanged: changed},
      () => {
        if (this.props.changedCallback) {
          this.props.changedCallback(this.state.formChanged)
        }
      }
    )
  }

  _validateForm = () => {
    let allValid = true
    const skip = ['agencyName', 'clientName']
    for (let field of this.fields) {
      if ((skip.indexOf(field) < 0) && !this.state[field].valid) {
        allValid = false
        break
      }
    }
    this.setState({formValid: allValid})
  }

  _agencyCreated = (agency) => {
    this.setState({
      agencyName: {value: agency.attributes.name, valid: true},
      agencyId: {value: agency.id, valid: true},
      clearAddAgency: true
    },
    () => {
      this._validateForm()
      setTimeout(() => document.dispatchEvent(this.closeAddAgencyEvent), 500)
    })
  }

  render() {
    return (
      <Card>
        {this.props.formTitle && <CardTitle title={this.props.formTitle} />}
        <CardText>
          <Row>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="firstName"
                required={true}
                errorMessage="First name is required"
                hintText="First name"
                floatingLabelText="Fist name (required)"
                fullWidth={true}
                value={this.state.firstName.value}
                validatedOnChange={this._change}
              />
            </Col>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="lastName"
                required={true}
                errorMessage="First name is required"
                hintText="Last name"
                floatingLabelText="Last name (required)"
                fullWidth={true}
                value={this.state.lastName.value}
                validatedOnChange={this._change}
              />
            </Col>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="title"
                hintText="Creative producer"
                floatingLabelText="Title"
                fullWidth={true}
                value={this.state.title.value}
                validatedOnChange={this._change}
              />
            </Col>
          </Row>

          <Row>
            <Col lg="4" md="12">
              <RelationalTextInput
                name="agencyId"
                hintText="SooperCreativeGuys LLC."
                floatingLabelText="Agency (must match)"
                fullWidth={true}
                value={this.state.agencyName.value}
                selectedId={this.state.agencyId.value}
                dataStore={AgencyStore}
                autoCompleteFilter='name_autocomplete'
                autoCompleteAttribute='name'
                validatedOnChange={this._change}
                onTextChange={(value) => this._setRelationalName('agencyName', 'agencyId', value)}
                formCloseEvent={this.closeAddAgencyEvent}
                formComponent={
                  <NewAgency
                    formTitle="Add a new agency"
                    attributes={{name: this.state.agencyName.value}}
                    forceChanged={true}
                    savedCallback={this._agencyCreated}
                  />
                }
              />
            </Col>
          </Row>

          <Row>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="email"
                hintText="email@example.com"
                floatingLabelText="E-mail"
                pattern={/.*@.*\..*/}
                errorMessage="Invalid email"
                fullWidth={true}
                value={this.state.email.value}
                validatedOnChange={this._change}
              />
            </Col>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="address"
                hintText="123 Street Rd."
                floatingLabelText="Street address"
                fullWidth={true}
                value={this.state.address.value}
                validatedOnChange={this._change}
              />
            </Col>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="city"
                hintText="Townsville"
                floatingLabelText="City"
                fullWidth={true}
                value={this.state.city.value}
                validatedOnChange={this._change}
              />
            </Col>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="province"
                hintText="ON"
                floatingLabelText="State/Province"
                pattern={/[a-zA-Z]{2}/}
                maxLength={2}
                errorMessage="Invalid state/province code"
                fullWidth={true}
                value={this.state.province.value}
                validatedOnChange={this._change}
              />
            </Col>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="country"
                hintText="Canada"
                floatingLabelText="Country"
                fullWidth={true}
                value={this.state.country.value}
                validatedOnChange={this._change}
              />
            </Col>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="postalCode"
                hintText="A1B 2C3"
                floatingLabelText="Postal code"
                fullWidth={true}
                value={this.state.postalCode.value}
                validatedOnChange={this._change}
              />
            </Col>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="mobilePhone"
                hintText="647-555-5678"
                floatingLabelText="Mobile phone"
                fullWidth={true}
                value={this.state.mobilePhone.value}
                validatedOnChange={this._change}
              />
            </Col>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="officePhone"
                hintText="647-555-5678"
                floatingLabelText="Office phone"
                fullWidth={true}
                value={this.state.officePhone.value}
                validatedOnChange={this._change}
              />
            </Col>
          </Row>

        </CardText>

        <CardActions style={{textAlign: 'center'}}>
          {this.props.submitCallback &&
            <SaveButton
              type="submit"
              disabled={!this.state.formChanged || !this.state.formValid}
              onTouchTap={this._submit}
            />
          }
          {this.props.cancelCallback &&
            <CancelButton
              onTouchTap={this.props.cancelCallback}
              disabled={!this.state.formChanged && this.state.formSubmitted}
            />
          }
        </CardActions>
      </Card>
    )
  }
}

export default AgencyProducerForm



// WEBPACK FOOTER //
// ./src/components/AgencyProducers/AgencyProducerForm.jsx